/**
 * La bascule visio ↔ bulle, en règle pure (Wajih, daily du 21/10, `proposé` pour la forme) :
 *
 *  - **l'orbite passe toujours d'abord**, là où le tandem est déjà : dans la bulle quand la visio va
 *    sortir, dans la visio quand elle va rentrer. L'élève voit qui passe devant ;
 *  - **puis la visio sort de la bulle** en grandissant jusqu'à sa place (`grow`), **ou y rentre** en
 *    rétrécissant, comme aspirée (`shrink`). Elle part du visage de devant de la bulle, et y revient ;
 *  - une vue changée à la main (la bascule « Activité » / « Visio », le visage du masteur touché,
 *    « Revenir à l'activité ») sort de la bulle ou y rentre de la même façon, sans orbite : le relais
 *    n'a pas changé ;
 *  - le relais seul (le masteur arrive pendant l'attente, Sam envoyé) fait son orbite où est le tandem,
 *    rien d'autre ;
 *  - en mouvement réduit, rien ne bouge (l'écran ne demande pas de bascule).
 *
 * Le plan (`stageMorphPlan`), ce qui est à l'écran à chaque temps (`stageMorphFrame`) et la géométrie de
 * la visio entre la bulle et sa place (`stageMorphGeometry`) sont purs ; `<StudentLivePage>` les fait
 * tourner (`useStageMorph`).
 */
import { stageLayout, STAGE_INSET } from "../../organisms/LiveStage/stageLayout"
import { type UITandemLead } from "../../organisms/LiveTandemBubble"
import { TANDEM_FACE, TANDEM_FRONT_FROM_RIGHT } from "../../organisms/LiveTandemBubble/tandemBubbleGeometry"

/**
 * La marge au-delà de sa durée avant qu'une bascule soit tenue pour bloquée, et posée d'office. La visio ne bouge
 * qu'une fois sa géométrie connue, puis rentre en animation : qu'une étape ne réponde pas, et elle resterait devant
 * l'activité jusqu'à la sortie du live. Hypothèse, pas une cause prouvée : l'élève qui voit la visio après chaque
 * scan, et que seul quitter le live débloque, n'est pas reproduit hors de l'iPhone. `onStuck` en laisse la trace.
 */
export const STAGE_MORPH_STUCK_MARGIN_MS = 1000

/** Ce que l'écran montre du tandem : la visio (sinon la bulle), et qui mène. */
export type TandemSight = { stage: boolean; lead: UITandemLead }

export type StageMorphPlan = {
    /** Où l'orbite se joue d'abord : `null` quand le relais ne change pas. */
    orbitIn: "stage" | "bubble" | null
    /** Puis la visio sort de la bulle (`grow`) ou y rentre (`shrink`) ; `null` : elle ne change pas. */
    morph: "grow" | "shrink" | null
}

export function stageMorphPlan(before: TandemSight, after: TandemSight): StageMorphPlan {
    const relay = before.lead !== after.lead
    const morph = before.stage === after.stage ? null : after.stage ? "grow" : "shrink"
    return { orbitIn: relay ? (before.stage ? "stage" : "bubble") : null, morph }
}

/** Une bascule en cours : son plan, et son temps (l'orbite, puis la visio qui sort ou rentre). */
export type StageMorphStep = { plan: StageMorphPlan; phase: "orbit" | "morph" }

export type StageMorphFrame = {
    /** La visio est posée : à sa place, ou en train de sortir ou de rentrer. */
    stage: boolean
    /** La bulle est posée (elle se mesure) ; `bubbleHidden` : elle ne se voit pas encore, ou plus. */
    bubble: boolean
    bubbleHidden: boolean
    /** Au grand écran, la colonne de la visio est ouverte (l'activité lui laisse sa place). */
    column: boolean
    /** La visio sort de la bulle ou y rentre, maintenant. */
    morph: "grow" | "shrink" | null
}

/**
 * Ce qui est à l'écran : au repos, la visio ou la bulle (`settled`). La visio qui sort : la bulle fait
 * son orbite seule, puis s'efface sous la visio qui grandit depuis elle, la colonne s'ouvrant du même
 * pas. La visio qui rentre : son orbite d'abord, la bulle posée mais cachée (elle se mesure) ; puis la
 * visio rétrécit sur la bulle, qui l'attend dessous, la colonne se refermant.
 */
export function stageMorphFrame(step: StageMorphStep | null, settled: TandemSight): StageMorphFrame {
    const morph = step?.plan.morph ?? null
    if (step == null || morph == null) {
        return { stage: settled.stage, bubble: !settled.stage, bubbleHidden: false, column: settled.stage, morph: null }
    }
    const moving = step.phase === "morph"
    if (morph === "grow") {
        return { stage: moving, bubble: true, bubbleHidden: moving, column: moving, morph: moving ? "grow" : null }
    }
    return { stage: true, bubble: true, bubbleHidden: !moving, column: !moving, morph: moving ? "shrink" : null }
}

type Point = { x: number; y: number }

/** Les deux bouts d'une valeur : dans la bulle (0), à sa place (1). */
type Ends = readonly [number, number]

export type StageMorphGeometry = {
    /** Le rond qui découpe la visio, dans le repère de sa place. */
    left: Ends
    top: Ends
    width: Ends
    height: Ends
    radius: Ends
    /** La visio, à sa taille, gardée dans le repère de sa place à l'intérieur du rond. */
    innerLeft: Ends
    innerTop: Ends
    /** Puis réduite et déplacée en son centre : ce qu'elle montre au point visé arrive sur la bulle. */
    translateX: Ends
    translateY: Ends
    scale: Ends
}

/**
 * La visio entre la bulle et sa place. `rect` : sa place ; `anchor` : le visage de devant de la bulle
 * (son centre et son diamètre), dans le même repère ; `focus` : le point de la visio qui se pose sur lui
 * (le centre de la vidéo du masteur, ou Sam devant) ; `cover` : ce que le rond en montre (la hauteur de la
 * vidéo, ou la taille de Sam), réduit au diamètre du visage. Tout est en ligne droite : une valeur se
 * lit entre ses deux bouts, à la part du mouvement.
 */
export function stageMorphGeometry(options: {
    rect: { width: number; height: number }
    anchor: Point & { size: number }
    focus: Point
    cover: number
}): StageMorphGeometry {
    const { rect, anchor, focus, cover } = options
    const d = anchor.size
    const k0 = d / Math.max(d, cover)
    const center = { x: rect.width / 2, y: rect.height / 2 }
    // Une échelle se prend au centre : le point visé, réduit, se pose sur le visage.
    const tx0 = anchor.x - center.x - k0 * (focus.x - center.x)
    const ty0 = anchor.y - center.y - k0 * (focus.y - center.y)
    const left: Ends = [anchor.x - d / 2, 0]
    const top: Ends = [anchor.y - d / 2, 0]
    return {
        left,
        top,
        width: [d, rect.width],
        height: [d, rect.height],
        radius: [d / 2, 0],
        innerLeft: [-left[0], -left[1]],
        innerTop: [-top[0], -top[1]],
        translateX: [tx0, 0],
        translateY: [ty0, 0],
        scale: [k0, 1],
    }
}

/** La hauteur de la vidéo du masteur posée entière dans la scène (un écran 16:9, `contain`). */
function stageVideoCover(width: number, height: number): number {
    return Math.min(height, (width * 9) / 16, width)
}

/** Une boîte mesurée à l'écran (`measureInWindow`). */
export type ScreenBox = { x: number; y: number; width: number; height: number }

/**
 * Le visage de devant de la bulle, quand il n'a pas pu se mesurer : la bulle au coin bas droit, à la marge
 * de sa fente, et le visage de devant à gauche de sa boîte (`tandemBubbleGeometry`).
 */
const FALLBACK_INSET = 16

/**
 * La géométrie d'une bascule, sur ce que la page a mesuré : la zone où se tiennent la visio et
 * l'activité (`region`, la visio à sa gauche), le visage de devant de la bulle (`front`, `null` s'il n'a
 * pas pu se mesurer), la largeur de la visio à sa place, et qui mène quand elle est en grand : Sam devant
 * se pose sur le visage (le Concentré), sinon la vidéo du masteur. `twins` : la bande de mes autres
 * appareils est sur la scène — le duo, posé au-dessus de ma vignette, monte avec elle.
 */
export function stageMorphGeometryFor(options: {
    region: ScreenBox
    front: ScreenBox | null
    stageWidth: number
    samLeads: boolean
    twins?: boolean
}): StageMorphGeometry {
    const { region, front, stageWidth, samLeads, twins = false } = options
    const rect = { width: stageWidth, height: region.height }
    const anchor =
        front != null
            ? { x: front.x + front.width / 2 - region.x, y: front.y + front.height / 2 - region.y, size: front.width }
            : {
                  x: region.width - FALLBACK_INSET - TANDEM_FRONT_FROM_RIGHT,
                  y: region.height - FALLBACK_INSET - TANDEM_FACE / 2,
                  size: TANDEM_FACE,
              }
    // La tuile de la scène, dans sa marge (`<LiveStage>`).
    const tile = { width: rect.width - 2 * STAGE_INSET, height: rect.height - 2 * STAGE_INSET }
    if (samLeads) {
        const sam = stageLayout(tile.width, tile.height, false, 0, twins).samFront
        return stageMorphGeometry({
            rect,
            anchor,
            focus: { x: STAGE_INSET + sam.center.x, y: STAGE_INSET + sam.center.y },
            cover: sam.size,
        })
    }
    return stageMorphGeometry({
        rect,
        anchor,
        focus: { x: rect.width / 2, y: rect.height / 2 },
        cover: stageVideoCover(tile.width, tile.height),
    })
}
